import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { getAllCaseStudies } from "@/content/case-studies";
import IllustrativeDisclaimer from "@/components/site/IllustrativeDisclaimer";

// Home showcase — surfaces the 3 newest deal structures as an editorial band
// between ClientWins (aggregate credibility) and ProductsTeaser. These are
// illustrative walkthroughs, not specific closed transactions — see the
// inline disclaimer under the H2.
export const HomeCaseStudies = () => {
  const studies = getAllCaseStudies().slice(0, 3);
  if (studies.length === 0) return null;

  return (
    <section
      className="py-32 md:py-40 border-t border-black/10"
      style={{ background: "linear-gradient(180deg, transparent, rgba(196,164,124,0.05) 60%, transparent)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
          <div>
            <div className="overline">Deal structures · Long-form</div>
            <h2 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95] max-w-2xl">
              How the files <em className="italic text-[color:var(--gold)]">get built</em>.
            </h2>
            <p className="mt-6 max-w-xl text-[color:var(--bone)]/70 leading-relaxed">
              Illustrative walkthroughs of how a working commercial finance broker structures common file types — situation, structure, outcome.
            </p>
          </div>
          <Link
            to="/deal-structures"
            data-testid="home-case-studies-view-all"
            className="font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)] gold-link"
          >
            All deal structures →
          </Link>
        </div>

        <div className="mb-12 max-w-2xl">
          <IllustrativeDisclaimer variant="inline" />
        </div>

        <div className="grid grid-cols-12 gap-6">
          {studies.map((s, i) => (
            <motion.article
              key={s.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.75, delay: i * 0.08 }}
              className={`col-span-12 ${
                i === 0 ? "md:col-span-6" : "md:col-span-3"
              }`}
            >
              <Link
                to={`/deal-structures/${s.slug}`}
                data-testid={`home-case-study-${s.slug}`}
                className="group block h-full"
              >
                <div
                  className={`relative overflow-hidden ${
                    i === 0 ? "aspect-[4/3]" : "aspect-[3/4]"
                  } mb-6`}
                >
                  <img
                    src={s.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/25" />
                  <div className="absolute top-5 left-5 right-5 flex items-start justify-between gap-3">
                    <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                      {s.industry}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]">
                      {s.readTime}
                    </span>
                  </div>
                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="font-serif text-3xl md:text-4xl text-[color:var(--gold)]">
                      {s.amount}
                    </div>
                    <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/85">
                      Funded in {s.timeToFund}
                    </div>
                  </div>
                </div>
                <h3
                  className={`font-serif leading-tight group-hover:text-[color:var(--gold)] transition-colors ${
                    i === 0 ? "text-3xl md:text-4xl" : "text-2xl"
                  }`}
                >
                  {s.title}
                </h3>
                {i === 0 && (
                  <p className="mt-4 text-[color:var(--bone)]/70 leading-relaxed max-w-xl">
                    {s.excerpt}
                  </p>
                )}
                <span className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--gold)]">
                  Read the walkthrough <ArrowRight size={13} />
                </span>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeCaseStudies;
